import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Box, Pagination } from "@mui/material";
import { Delete, DeleteSweep, ArrowBack } from "@mui/icons-material";
import { getBouncedEmails, getSpamReport } from "../api/action/query";
import { deleteSpamByEmail, deleteBouncedByEmail, deleteAllSpam, deleteAllBounced } from "../api/action/mutation";
import EmptyMessage from "../../common/EmptyMessage";
import BrandedLoading from "../../common/BrandedLoading";
import { ReasonTableCell, EmailTableCell, ActionTableCell } from "./tableCells";
import { useDispatch } from "react-redux";
import { messageShow } from "../../common/api/actions";
import { useHistory } from "react-router-dom";

const EmailReportTemplate = ({ type, permissions }) => {
    const [emailData, setEmailData] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const ROWS_PER_PAGE = 10;
    const dispatch = useDispatch();
    const history = useHistory();

    const fetchEmails = async () => {
        try {
            setLoading(true);
            const response = type === 'bounced' ? await getBouncedEmails() : await getSpamReport();
            setEmailData(response);
            setPage(1);
        } catch (error) {
            dispatch(messageShow(error.toString()))
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmails();
    }, [type]);

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString();
    };

    const handleDelete = async (email) => {
        try {
            if (type === 'bounced') {
                await deleteBouncedByEmail(email);
            } else {
                await deleteSpamByEmail(email);
            }
            setEmailData(prevData => prevData.filter(item => item.email !== email));
            dispatch(messageShow(`${type === 'bounced' ? 'Bounced' : 'Spam'} email deleted successfully`));
        } catch (error) {
            console.error(`Error deleting ${type} email:`, error);
            dispatch(messageShow(error.toString()));
        }
    };

    const handleDeleteAll = async () => {
        try {
            if (window.confirm(`Are you sure you want to delete all ${type} emails?`)) {
                if (type === 'bounced') {
                    await deleteAllBounced();
                } else {
                    await deleteAllSpam();
                }
                setEmailData([]);
                dispatch(messageShow(`All ${type} emails deleted successfully`));
            }
        } catch (error) {
            console.error(`Error deleting all ${type} emails:`, error);
            dispatch(messageShow(error.toString()));
        }
    };

    const tableHead = useMemo(() => (
        <TableHead>
            <TableRow>
                <TableCell>Date</TableCell>
                <EmailTableCell>Email</EmailTableCell>
                {type === "bounced" && <ReasonTableCell>Reason</ReasonTableCell>}
                <ActionTableCell>Action</ActionTableCell>
            </TableRow>
        </TableHead>
    ), [type]);

    const totalPages = emailData ? Math.ceil(emailData.length / ROWS_PER_PAGE) : 0;
    const paginatedData = emailData ? emailData.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE) : [];

    useEffect(() => {
        if (emailData && page > totalPages && totalPages > 0) {
            setPage(totalPages);
        }
    }, [emailData, page, totalPages]);

    if (isLoading) return <BrandedLoading />;

    return (
        <>  
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <IconButton
                        color="primary"
                        onClick={() => history.goBack()}
                        aria-label="back to previous page"
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" style={{ marginLeft: '10px' }}>
                        {type === 'bounced' ? 'Bounced Emails' : 'Spam Reports'}
                    </Typography>
                </Box>
                {emailData && Array.isArray(emailData) && emailData.length > 0 && (
                    <Tooltip title="Delete All">
                        <IconButton
                            aria-label="delete all"
                            size="large"
                            onClick={handleDeleteAll}
                            style={{ marginRight: '10px' }}
                            disabled={!permissions.canDelete}
                        >
                            <DeleteSweep color= {!permissions.canDelete?"disabled":"error"} size="large"/>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {!emailData || !Array.isArray(emailData) || emailData.length === 0 ? (
                <EmptyMessage message={`No ${type} emails to show.`} />
            ) : (
                <TableContainer component={Paper}>
                    <Table stickyHeader aria-label={`${type} emails table`}>
                        {tableHead}
                        <TableBody>
                            {paginatedData.map((email, index) => (
                                <TableRow key={index}>
                                    <TableCell>{formatTimestamp(email.created)}</TableCell>
                                    <EmailTableCell component="th" scope="row">
                                        {email.email}
                                    </EmailTableCell>
                                    {type === "bounced" && <ReasonTableCell>{email.reason}</ReasonTableCell>}
                                    <ActionTableCell>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                aria-label="delete"
                                                size="small"
                                                onClick={() => handleDelete(email.email)}
                                                disabled = {!permissions.canDelete}
                                            >
                                                <Delete color= {!permissions.canDelete?"disabled":"error"} />
                                            </IconButton>
                                        </Tooltip>
                                    </ActionTableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                    />
                </Box>
            )}
        </>
    );
};

export default EmailReportTemplate;
